/**
 * Loyalty Program — tier model.
 * Palette mirrors the FitUP loyalty design system (flat pastels, confident saturation).
 * Used by [[TierBadge]] and [[MemberListPage]] tier filter.
 */
export enum LoyaltyTier {
  BEGINNING = 'BEGINNING',
  SILVER = 'SILVER',
  GOLD = 'GOLD',
  PLATINUM = 'PLATINUM',
}

export interface TierStyle {
  /** Pill background fill (chip surface). */
  bgColor: string
  /** Text color readable on `bgColor` — for use inside the chip only. */
  textColor: string
  /**
   * Tier accent color tuned to be recognizable on a white background.
   * Use this when rendering the tier name as plain text (progress labels,
   * markers, history rails), NOT `textColor` (which can be near-black and
   * loses the tier identity outside the chip).
   */
  accentColor: string
  /** Mid-tone fill — for charts, progress gradient stops, swatches. */
  fillColor: string
  labelKey: string
}

export const TIER_STYLES: Record<LoyaltyTier, TierStyle> = {
  [LoyaltyTier.BEGINNING]: {
    bgColor: '#E5BF8E',
    textColor: '#4A2D11',
    accentColor: '#6B4019',
    fillColor: '#C58451',
    labelKey: 'loyalty-tier-beginning',
  },
  [LoyaltyTier.SILVER]: {
    bgColor: '#C5CDD6',
    textColor: '#2E3845',
    accentColor: '#455161',
    fillColor: '#97A1AF',
    labelKey: 'loyalty-tier-silver',
  },
  [LoyaltyTier.GOLD]: {
    bgColor: '#F0CB5A',
    textColor: '#3D2A00',
    accentColor: '#8A5D00',
    fillColor: '#D9A431',
    labelKey: 'loyalty-tier-gold',
  },
  [LoyaltyTier.PLATINUM]: {
    bgColor: '#DDE3EE',
    // Matches the progress-bar accent so the chip reads as silver-slate
    // rather than near-black on the periwinkle bg.
    textColor: '#4A5468',
    accentColor: '#4A5468',
    fillColor: '#4A5468',
    labelKey: 'loyalty-tier-platinum',
  },
}

export const TIER_ORDER: LoyaltyTier[] = [
  LoyaltyTier.PLATINUM,
  LoyaltyTier.GOLD,
  LoyaltyTier.SILVER,
  LoyaltyTier.BEGINNING,
]

/**
 * Tier thresholds (inclusive lower-bound on lifetime balance).
 * BEGINNING is the floor (0+), PLATINUM is the cap (no next tier).
 * Adjustable by the club later via Club Settings → Loyalty (QTY-1717).
 */
export const TIER_THRESHOLDS: Record<LoyaltyTier, number> = {
  [LoyaltyTier.BEGINNING]: 0,
  [LoyaltyTier.SILVER]: 500,
  [LoyaltyTier.GOLD]: 2000,
  [LoyaltyTier.PLATINUM]: 5000,
}

/** Tier reachable from `tier` (null when already at the top). */
export const nextTierFor = (tier: LoyaltyTier): LoyaltyTier | null => {
  switch (tier) {
    case LoyaltyTier.BEGINNING:
      return LoyaltyTier.SILVER
    case LoyaltyTier.SILVER:
      return LoyaltyTier.GOLD
    case LoyaltyTier.GOLD:
      return LoyaltyTier.PLATINUM
    case LoyaltyTier.PLATINUM:
      return null
  }
}

export const tierFromBalance = (balance: number): LoyaltyTier => {
  if (balance >= TIER_THRESHOLDS[LoyaltyTier.PLATINUM]) return LoyaltyTier.PLATINUM
  if (balance >= TIER_THRESHOLDS[LoyaltyTier.GOLD]) return LoyaltyTier.GOLD
  if (balance >= TIER_THRESHOLDS[LoyaltyTier.SILVER]) return LoyaltyTier.SILVER
  return LoyaltyTier.BEGINNING
}
